import React, { Fragment, useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Sortable } from '@shopify/draggable';
import arrayMove from 'array-move';

import DropZone from '../common/DropZone';

import { getYandexStoreImageMin } from '../../helpers';
import { toast } from 'react-toastify';

const ImagesWrapper = styled('div')`
  .sortable-wrapper {
    display: flex;
    flex-wrap: wrap;
    > .draggable-source {
      position: relative;
      height: 100px;
      width: 100px;
      margin: 5px;
      background-repeat: no-repeat;
      background-position: center;
      background-color: #f2f3f4;
      background-size: contain;
      cursor: pointer;
      border-radius: 0.2rem;
      box-shadow: rgba(116, 117, 126, 0.2) 0px 1px 0px 0px;
      &:hover {
        .fe-copy {
          z-index: 100;
          color: white;
        }
      }
      .fe-copy {
        position: absolute;
        top: 3px;
        right: 3px;
        font-size: 16px;
        z-index: 10;
      }
      .fe-star-on {
        position: absolute;
        top: -8px;
        left: -8px;
        color: #ffa500;
        font-size: 18px;
        z-index: 10;
      }
      .zoom-layer {
        visibility: hidden;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0);
        transition-duration: 0.3s;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        text-align: center;
        border-radius: 0.2rem;
        .fe-zoom-in {
          font-size: 25px;
          color: #fff;
        }
      }
      &:hover:not(.draggable-source--is-dragging) {
        .zoom-layer {
          visibility: visible;
          background-color: rgba(0, 0, 0, 0.45);
        }
      }
    }
  }
  .dropzone {
    padding: 0;
    margin: 5px;
    width: 100px;
    height: 100px;
    min-height: 100px;
    cursor: pointer;
    background-color: #ffffff;
    border: 2px dashed rgba(50, 58, 70, 0.3);
    &.with-text {
      width: 100%;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      transition-duration: 0.3s;
    }
  }
`;

const ImageGallery = (props) => {
  const {
    isSingle,
    showDefault,
    defaultIndex,
    showHover,
    dropzoneDescription,
    allowReordering = true,
    images = [],
    onUpload = () => false,
    onReorder = () => false,
    onItemClick = () => false,
    onDelete = () => false,
    copyImage = false,
  } = props;

  const [items, updateItems] = useState(images);
  const [onHold, updateHold] = useState(false);
  const [isUploading, setUploadingStatus] = useState(false);
  const sortableEl = useRef(null);

  useEffect(() => {
    updateItems(images);
  }, [images]);

  useEffect(() => {
    if (!onHold && !isSingle && allowReordering) {
      const sortable = new Sortable(sortableEl.current, {
        delay: 150,
      });
      sortable.on('sortable:stop', (evt) => {
        const { oldIndex, newIndex } = evt;
        const newArray = arrayMove(items, oldIndex, newIndex);
        updateItems(newArray);
        reorderImages(newArray);
      });
      return () => sortable.destroy();
    }
  });

  const uploadImages = async (data) => {
    setUploadingStatus(true);
    await onUpload(data);
    setUploadingStatus(false);
  };

  const reorderImages = (reorderArr) => {
    updateHold(true);
    onReorder(reorderArr);
    updateHold(false);
  };

  const clickItem = (image, i) => {
    onItemClick(image, i);
  };

  const copyImageLink = (url, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    toast.info('Изображение успешно скопировано');
  };

  return (
    <ImagesWrapper>
      <div className="sortable-wrapper" ref={sortableEl}>
        {items.map((item, i) => (
          <Fragment key={item.id}>
            <div
              className="draggable-source"
              onClick={() => clickItem(item, i)}
              style={{ backgroundImage: `url(${getYandexStoreImageMin(item.url, 350)})` }}
              data-testid={`uploadImageTestId-${item.id}`}
            >
              {i === defaultIndex && showDefault ? <i className="fe-star-on" /> : null}
              {copyImage && <i className="fe-copy" onClick={(e) => copyImageLink(item.url, e)} />}
              {showHover ? (
                <div className="zoom-layer" data-testid="zoom">
                  <i className="fe-zoom-in" />
                </div>
              ) : null}
            </div>
          </Fragment>
        ))}
        <DropZone
          handleFileDrop={uploadImages}
          showDescription={!items.length}
          dropzoneDescription={dropzoneDescription}
          isSingle={isSingle}
          isUploading={isUploading}
        />
      </div>
    </ImagesWrapper>
  );
};

export default ImageGallery;
